import { useMemo } from 'react'
import { RouterProvider } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Provider as ReduxProvider, useSelector } from 'react-redux'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { router } from '@/router'
import { queryClient } from '@/lib/query'
import { store, type RootState } from '@/store'
import { createAppTheme } from '@/styles/theme'
import { ENV } from '@/config'
import { BrandProvider, useBrand, useBrandDocument } from '@/branding'
import { GlobalLoader } from '@/components/ui/GlobalLoader'
import { GlobalSnackbar } from '@/components/ui/GlobalSnackbar'
import { GlobalZoomCall } from '@/integrations/zoom/GlobalZoomCall'

/**
 * ThemeWrapper — builds the MUI theme from the resolved organization brand
 * and the themeMode in Redux. Must be inside both BrandProvider and
 * ReduxProvider.
 */
const ThemeWrapper = ({ children }: { children: React.ReactNode }) => {
  const themeMode = useSelector((s: RootState) => s.ui.themeMode)
  const brand = useBrand()
  const theme = useMemo(() => createAppTheme(themeMode, brand), [themeMode, brand])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  )
}

/** AppShell — everything below the theme. Applies the brand to the document
 *  shell (favicon, typeface, title), which must happen inside BrandProvider. */
const AppShell = () => {
  useBrandDocument()

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <GlobalLoader />
      <GlobalSnackbar />
      {/* Rendered outside RouterProvider so a minimized call survives
          navigation between pages (see GlobalZoomCall). */}
      <GlobalZoomCall />
      {ENV.FLAGS.REACT_QUERY_DEVTOOLS && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}

const App = () => {
  return (
    <BrandProvider>
      <ReduxProvider store={store}>
        <ThemeWrapper>
          <AppShell />
        </ThemeWrapper>
      </ReduxProvider>
    </BrandProvider>
  )
}

export default App
